import { Modal, Form, Input, Select, message } from "antd";
import { ExpenseDescription } from "@/types/expenseDescription";
import { useUpdateExpenseDescription } from "@/pages/Expenses/ManageExpense/hooks/useExpenseDescriptions";
import { useGetExpenseTypesOptions } from "@/pages/Expenses/ManageExpense/hooks/useExpenseTypes";
import { useEffect, useMemo } from "react";
import { SelectOption } from "@/types/select";
import { UpdateExpenseDescriptionFormValues } from "@/pages/Expenses/ManageExpense/api/expenseDescriptions";
import { useGetExpenseUnits } from "@/pages/Expenses/RecordExpense/hooks/useExpenses";

interface EditModalProps {
  data?: ExpenseDescription;
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
}

const EditModal = ({ data, isOpen, onClose, onSuccess }: EditModalProps) => {
  const [form] = Form.useForm();
  const expenseTypeId = Form.useWatch("expense_type_id", form);
  const { mutate: updateExpenseDescription, isPending } =
    useUpdateExpenseDescription();
  const { data: expenseTypes } = useGetExpenseTypesOptions();
  const { data: units } = useGetExpenseUnits({
    config: {
      params: {
        limit: -1,
        include_inactive: false,
      },
    },
    enabled: isOpen,
  });

  const expenseTypeOptions: SelectOption[] = (expenseTypes || [])
    .filter((type) => type.is_active)
    .map((type) => ({
      label: type.name,
      value: type.id,
    }));

  const unitOptions: SelectOption[] = (units || [])
    .filter((unit) => unit.is_active)
    .map((unit) => ({
      label: unit.name,
      value: unit.id,
    }));

  // Hide unit if not required in expense type
  const isUnitHidden = useMemo(() => {
    if (!expenseTypeId) {
      return true;
    }

    const isUnitRequired = expenseTypes
      ?.find((type) => type.id === expenseTypeId)
      ?.required_fields?.includes("unit");

    return !isUnitRequired;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units, expenseTypeId]);

  // Disable unit if description is already has unit
  const isUnitDisabled = useMemo(() => {
    if (!data) {
      return false;
    }

    return !!data?.unit_id;
  }, [data]);

  const handleSubmit = async (values: UpdateExpenseDescriptionFormValues) => {
    const payload: UpdateExpenseDescriptionFormValues = {
      ...values,
      id: data?.id || "",
      unit_id: isUnitHidden ? null : values.unit_id,
    };

    updateExpenseDescription(payload, {
      onSuccess: () => {
        message.success("Successfully updated expense description");
        form.resetFields();
        onClose?.();
        onSuccess?.();
      },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
    }
  }, [isOpen, form]);

  useEffect(() => {
    if (!data) {
      return;
    }

    form.setFieldsValue({
      name: data.name,
      expense_type_id: data.expense_type?.id,
      unit_id: data.unit?.id,
    });
  }, [data, form]);

  return (
    <Modal
      title="Edit Description"
      open={isOpen}
      onCancel={handleCancel}
      width={600}
      confirmLoading={isPending}
      onOk={form.submit}
      okText="Update"
      destroyOnHidden
    >
      <Form<UpdateExpenseDescriptionFormValues>
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        initialValues={
          data && {
            name: data.name,
            expense_type_id: data.expense_type?.id,
            unit_id: data.unit?.id,
          }
        }
      >
        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="expense_type_id"
          label="Expense Type"
          rules={[{ required: true }]}
        >
          <Select
            placeholder="Select expense type"
            options={expenseTypeOptions}
          />
        </Form.Item>
        <Form.Item
          name="unit_id"
          label="Unit"
          rules={[{ required: !isUnitHidden && !isUnitDisabled }]}
          hidden={isUnitHidden}
        >
          <Select
            placeholder="Select unit"
            options={unitOptions}
            disabled={isUnitDisabled}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditModal;
